import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { PriceSchedule } from 'src/shared/models/price-schedule.model';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  priceValidator,
  siblingPriceNotEqualValidator,
  upfrontAmountValidator,
} from 'src/app/validators/price-validator';
import { priceMinimumValidator } from 'src/app/validators/price-minimum.validator';
import { DecimalPipe } from '@angular/common';
import { endDateAfterStartDateValidator } from 'src/app/validators/end-date-after-start-date-validator';
import { dateOverlapValidator } from 'src/app/validators/date-overlap-validator';
import { dateInThePastValidator } from 'src/app/validators/date-in-the-past.validator';
import { dateFormatValidator } from 'src/app/validators/date-format.validator';
import { nameUniquenessValidator } from 'src/app/validators/name-uniqueness.validator';
import { savedNamesUniquenessValidator } from 'src/app/validators/saved-names-uniqueness-validator';
import { DateRange } from 'src/shared/models/date-range.model';
import { Subscription } from 'rxjs';
import { SiblingPriceValidatorService } from '../../../shared/services/price-validation.service';
import { SeFeDatepickerYearRange, SeFeExcludedDates } from 'se-fe-datepicker';
import { SeFeThemesLoader } from 'se-fe-themes';
import { DateTime } from 'luxon';

const PRICE_LOCALE = 'en-US';
const ZERO_RATE_TYPES = ['background_screen', 'abuse_prevention_system'];

export enum DateType {
  START = 'START',
  END = 'END',
}

@Component({
  selector: 'app-price-variation',
  templateUrl: './price-variation.component.html',
  styleUrls: ['price-variation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PriceVariationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() priceVariation: PriceSchedule;
  @Input() priceVariationDates: DateRange[];
  @Input() priceVariationNames: string[];
  @Input() componentIndex: number;
  @Input() saleItemType;
  @Input() partialAmountDueUpFrontSelected: boolean;
  @Input() savedPriceSchedules: Partial<PriceSchedule>[];
  @Output() remove = new EventEmitter();

  public formGroup: FormGroup;
  public dateRangeConfig: {
    yearRange: SeFeDatepickerYearRange;
    messages: Record<string, string>;
  } = {
    yearRange: { future: 10, past: 10, sort: 'desc' },
    messages: {
      required: 'Please select a date',
      dateInvalid: 'Please fix the date format',
      dateOutOfRange: 'Date is unavailable',
    },
  };
  priceVariationGreaterThanTwentyFive = false;
  isZeroRateType = false;

  maxPartial: number;
  private siblingPricesubscription: Subscription;
  dateType = DateType;
  initialStartDate;
  initialEndDate;

  constructor(
    private elementRef: ElementRef,
    private formBuilder: FormBuilder,
    private decimalPipe: DecimalPipe,
    private siblingPriceValidatorService: SiblingPriceValidatorService
  ) {
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });
  }

  ngOnInit() {
    this.setComponentStyles();
    this.setFormGroup();
    const priceInput = this.formGroup.get('price_cents');
    this.maxPartial = priceInput.valid ? priceInput.value - 10 : 0;
    this.togglePriceVariationUpfrontPrice();
    this.initialStartDate = this.priceVariation.start_date;
    this.initialEndDate = this.priceVariation.end_date;

    this.siblingPricesubscription =
      this.siblingPriceValidatorService.siblingPrices$.subscribe(() => {
        priceInput?.updateValueAndValidity();
      });
  }

  ngOnChanges() {
    this.isZeroRateType = ZERO_RATE_TYPES.includes(this.saleItemType);
    const formGroup = this.formGroup;
    if (formGroup) {
      this.togglePriceVariationUpfrontPrice();
      const nameControl = formGroup.get('pricing_name');
      const priceControl = formGroup.get('price_cents');
      const upfrontAmountControl = formGroup.get('upfront_amount_cents');
      const startDateControl = formGroup.get('start_date');
      const nameControlValidators = [
        this.requiredTrimmedValueValidator,
        Validators.maxLength(64),
        nameUniquenessValidator(this.priceVariationNames, this.componentIndex),
        savedNamesUniquenessValidator(this.savedPriceSchedules, this.priceVariation.price_schedule_id),
      ];
      const priceControlValidators = [
        Validators.required,
        priceValidator(),
        siblingPriceNotEqualValidator(
          this.siblingPriceValidatorService.siblingPrices$,
          this.componentIndex
        ),
        ...(this.partialAmountDueUpFrontSelected ? [priceMinimumValidator(25)] : []),
      ];
      const upfrontAmountControlValidators = [
        ...(this.partialAmountDueUpFrontSelected &&
        this.priceVariationGreaterThanTwentyFive
          ? [Validators.required]
          : []),
        Validators.pattern(/^(\$)?(([0-9,]+)?([0-9]\.)?(\.[0-9]+)?)$/),
        priceValidator(true),
        upfrontAmountValidator('price_cents'),
        Validators.min(0.5),
      ];
      const startDateControlValidators = [
        Validators.required,
        dateFormatValidator(),
        dateOverlapValidator(this.priceVariationDates, this.componentIndex),
      ];
      nameControl.setValidators(nameControlValidators);
      priceControl.setValidators(priceControlValidators);
      upfrontAmountControl.setValidators(upfrontAmountControlValidators);
      if (!this.priceVariation.is_editing ||
         (this.priceVariation.is_editing && !this.normalizedDatesEqual(this.initialStartDate, startDateControl.value))) {
        startDateControlValidators.splice(2, 0, dateInThePastValidator());
      }
      startDateControl.setValidators(startDateControlValidators);
      nameControl.updateValueAndValidity();
      priceControl.updateValueAndValidity();
      upfrontAmountControl.updateValueAndValidity();
      startDateControl.updateValueAndValidity();
      formGroup.get('end_date').updateValueAndValidity();
      this.configureInputFields();
      if (!this.priceVariation.is_editing) {
        if (priceControl.valid) {
          upfrontAmountControl.enable();
        } else {
          upfrontAmountControl.setValue(null);
          upfrontAmountControl.disable();
        }
      }
    }
  }

  ngOnDestroy() {
    this.siblingPricesubscription.unsubscribe();
  }

  handleRemovePriceVariation() {
    this.remove.emit();
  }

  handlePriceChange() {
    const priceInput = this.formGroup.get('price_cents');
    this.siblingPriceValidatorService.updatePrice(
      this.componentIndex,
      priceInput.value
    );
    if (priceInput.valid) {
      const price = priceInput.value.replace(/,/g, '');
      this.maxPartial = price - 10;
      priceInput.setValue(
        this.decimalPipe.transform(price, '1.2-2', PRICE_LOCALE)
      );

      this.togglePriceVariationUpfrontPrice();
    }
  }

  handleUpfrontChange() {
    const upfrontPriceInput = this.formGroup.get('upfront_amount_cents');
    if (upfrontPriceInput.valid || !isNaN(Number(upfrontPriceInput.value))) {
      upfrontPriceInput.setValue(
        this.decimalPipe.transform(
          upfrontPriceInput.value.replace(/,/g, ''),
          '1.2-2',
          PRICE_LOCALE
        )
      );
    }
  }

  getTrimmedNameLength(): number {
    const pricingNameControlValue = this.formGroup.get('pricing_name').value;
    if (pricingNameControlValue) {
      return pricingNameControlValue.trim().length;
    }
    return 0;
  }

  requiredTrimmedValueValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return typeof value === 'string' && value.trim().length > 0 ? null : { required: true };
}

  getExcludedDates(isEditing: boolean, type: DateType): SeFeExcludedDates[] {
    if (isEditing) {
      const referenceDate = this.getLocalReferenceDate(type);
      if (this.isBeforeToday(referenceDate)) {
        return this.getPastDatesExcludingSavedDate(referenceDate);
      }
    }

    return this.getPastExcludedDates();
  }

  dateUpdated(type: DateType) {
    switch (type) {
      case 'START':
        const startDate = this.formGroup.get('start_date')?.value;
        return !this.normalizedDatesEqual(this.initialStartDate, startDate);
      case 'END':
        const endDate = this.formGroup.get('end_date')?.value;
        return !this.normalizedDatesEqual(this.initialEndDate, endDate);
      default:
        return true;
    }
  }

  trimName() {
    const nameControl = this.formGroup.get('pricing_name');
    nameControl.setValue(nameControl.value.trim(), { emitEvent: false });
  }

  formatAmount(control: string) {
    const amountControl = this.formGroup.get(control);
    amountControl.setValue(
      this.decimalPipe.transform(amountControl.value, '1.2-2', PRICE_LOCALE),
      { emitEvent: false }
    );
  }

  private normalizedDatesEqual(initialDate: string | Date, formDate: string | Date): boolean {
    const parsedInitialDate = this.normalizedDateValue(initialDate);
    const parsedFormDate = this.normalizedDateValue(formDate);
    return !!parsedInitialDate && parsedInitialDate === parsedFormDate;
  }

  private normalizedDateValue(date: string | Date): string | null {
    if (!date) {
      return null;
    }

    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : DateTime.fromJSDate(date).toISODate();
    }

    const isoDate = date.match(/^(\d{4}-\d{2}-\d{2})(?:T.*)?$/);
    if (isoDate) {
      const parsedIsoDate = DateTime.fromISO(isoDate[1]);
      return parsedIsoDate.isValid ? parsedIsoDate.toISODate() : null;
    }

    const displayDate = DateTime.fromFormat(date, 'MM/dd/yyyy');
    if (displayDate.isValid) {
      return displayDate.toISODate();
    }

    const parsedDate = DateTime.fromJSDate(new Date(date));
    return parsedDate.isValid ? parsedDate.toISODate() : null;
  }

  private getLocalReferenceDate(type: DateType) {
    const dateString =
      type === DateType.START
        ? this.priceVariation.start_date
        : this.priceVariation.end_date;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private setComponentStyles(): void {
    const addPriceVariationButton: HTMLElement =
      this.elementRef.nativeElement.querySelector(
        '.remove-price-variation-button'
      ).firstChild;
    if (addPriceVariationButton) {
      addPriceVariationButton.style.textTransform = 'none';
    }
  }

  private setFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      pricing_name: [this.priceVariation.pricing_name, this.requiredTrimmedValueValidator],
      price_cents: [this.priceVariation.price_cents || ''],
      upfront_amount_cents: [
        (this.partialAmountDueUpFrontSelected &&
          this.priceVariation.upfront_amount_cents) ||
          '',
      ],
      start_date: [this.priceVariation.start_date],
      end_date: [
        this.priceVariation.end_date,
        [Validators.required, endDateAfterStartDateValidator('start_date')],
      ],
    });
    if (!this.priceVariation.is_editing) {
      this.formGroup.get('upfront_amount_cents').disable();
    }
  }

  private getTenYearsAgo(referenceDate?: Date): string {
    const nowOrReferenceDate = referenceDate ?? new Date();
    const firstDay = new Date(nowOrReferenceDate.getFullYear() - 10, 0, 1);
    return firstDay.toISOString().split('T')[0];
  }

  private getPastDatesExcludingSavedDate(referenceDate: Date): SeFeExcludedDates[] {
    const before = new Date(
      Date.UTC(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate() - 1
      )
    );
    const after = new Date(
      Date.UTC(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate() + 1
      )
    );

    return [
      {
        start: this.getTenYearsAgo(referenceDate),
        end: before.toISOString().split('T')[0],
      },
      {
        start: after.toISOString().split('T')[0],
        end: this.getYesterday(),
      },
    ];
  }

  private getPastExcludedDates(): SeFeExcludedDates[] {
    return [
      {
        start: this.getTenYearsAgo(),
        end: this.getYesterday(),
      },
    ];
  }

  private getYesterday(): string {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  }

  private isBeforeToday(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const referenceDate = new Date(date);
    referenceDate.setHours(0, 0, 0, 0);
    return referenceDate < today;
  }

  private togglePriceVariationUpfrontPrice() {
    const priceInput = this.formGroup.get('price_cents');
    const upfrontPriceInput = this.formGroup.get('upfront_amount_cents');
    if (priceInput.value < 25) {
      this.priceVariationGreaterThanTwentyFive = false;
    } else {
      this.priceVariationGreaterThanTwentyFive = true;
      upfrontPriceInput.enable();
    }
  }

  private configureInputFields(): void {
    const inputElements = this.elementRef.nativeElement.querySelectorAll(
      '.price-variation-container .input-field input'
    );
    inputElements.forEach((input: HTMLInputElement) => {
      input.setAttribute('maxlength', '64');
      const initialWidth = input.offsetWidth;
      input.style.maxWidth = `${initialWidth}px`;
    });
  }
}
